import React, { useState } from "react"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { Box, Stack, Button, FormControl } from "@mui/material"
import CustomAutocomplete from "src/components/CustomAutocomplete"
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded"
import TableRowsRoundedIcon from "@mui/icons-material/TableRowsRounded"
import ViewButton from "src/components/table/ViewButton"
import SearchTextField from "src/components/table/SearchTextField"
import dayjs, { Dayjs } from "dayjs"
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded"
import TuneIcon from "@mui/icons-material/Tune"
import { useResponsive } from "src/utils/useResponsive"
import MyFilesFilterModal from "./MyFilesFilterModal"

export interface StatusOption {
  label: string
  value: string
}

export interface RequesterRecord {
  userId: string
  firstName: string
  lastName: string
  userName: string
  roles: string[]
}

export interface MyFilesFilterProps {
  searchQuery: string
  onSearchQueryChange: (value: string) => void
  statusFilter: StatusOption[]
  onStatusFilterChange: (statuses: StatusOption[]) => void
  statusOptions: StatusOption[]
  uploadDateFrom: Dayjs | null
  onUploadDateFromChange: (date: Dayjs | null) => void
  uploadDateTo: Dayjs | null
  onUploadDateToChange: (date: Dayjs | null) => void
  requesterList?: RequesterRecord[]
  requesterFilter?: RequesterRecord
  onRequesterFilterChange: (requester: RequesterRecord | undefined) => void
  onClearFilters: () => void
  viewType: "grid" | "list"
  onViewTypeChange: (viewType: "grid" | "list") => void
}

export function getRequesterName(requesterList: RequesterRecord[] | undefined, requesterId: string): string {
  const requester = requesterList?.find((r) => r.userId === requesterId)
  return requester ? `${requester.firstName} ${requester.lastName}` : ""
}

const MyFilesFilter: React.FC<MyFilesFilterProps> = ({
  searchQuery,
  onSearchQueryChange,
  statusFilter,
  onStatusFilterChange,
  statusOptions,
  uploadDateFrom,
  onUploadDateFromChange,
  uploadDateTo,
  onUploadDateToChange,
  requesterList,
  requesterFilter,
  onRequesterFilterChange,
  onClearFilters,
  viewType,
  onViewTypeChange
}) => {
  const { isMobile } = useResponsive()
  const [filterModalOpen, setFilterModalOpen] = useState(false)

  const viewToggle = (
    <Stack direction="row" spacing={1.25}>
      <ViewButton active={viewType === "grid"} onClick={() => onViewTypeChange("grid")}>
        <GridViewRoundedIcon />
      </ViewButton>
      <ViewButton active={viewType === "list"} onClick={() => onViewTypeChange("list")}>
        <TableRowsRoundedIcon />
      </ViewButton>
    </Stack>
  )

  return (
    <Box
      sx={{
        backgroundColor: "#E9FAFF",
        boxShadow: "0px 2px 10px #0719391A",
        p: { xs: 2, md: 6 },
        pb: 4
      }}
    >
      <Stack direction="row" spacing={2} justifyContent="space-between" sx={isMobile ? { width: "100%" } : undefined}>
        <Stack
          direction={isMobile ? "column" : "row"}
          spacing={1.25}
          alignItems="center"
          sx={isMobile ? { width: "100%" } : undefined}
        >
          {isMobile && (
            <Stack direction="row" spacing={1.25} alignItems="center" sx={isMobile ? { width: "100%" } : undefined}>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<TuneIcon />}
                onClick={() => setFilterModalOpen(true)}
                sx={{ minWidth: "auto", px: 2 }}
                fullWidth={isMobile}
              >
                Filters
              </Button>
              {viewToggle}
            </Stack>
          )}

          <Stack direction="row" spacing={1.25} alignItems="center" sx={isMobile ? { width: "100%" } : undefined}>
            <Box sx={isMobile ? { width: "100%" } : undefined}>
              <FormControl fullWidth={isMobile}>
                <SearchTextField
                  value={searchQuery}
                  onChange={onSearchQueryChange}
                  onClear={() => onSearchQueryChange("")}
                />
              </FormControl>
            </Box>

            {!isMobile && (
              <>
                <Box sx={{ width: "300px" }}>
                  <CustomAutocomplete<StatusOption>
                    value={statusFilter}
                    options={statusOptions}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    onChange={(e, val) => {
                      onStatusFilterChange(val as StatusOption[])
                    }}
                    label="Status"
                    size="small"
                    multiple
                  />
                </Box>
                <Box sx={{ width: "200px" }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Upload date from"
                      value={uploadDateFrom}
                      onChange={onUploadDateFromChange}
                      slotProps={{
                        textField: { size: "small" },
                        field: { clearable: true, onClear: () => onUploadDateFromChange(null) }
                      }}
                      maxDate={uploadDateTo ? uploadDateTo : dayjs()}
                    />
                  </LocalizationProvider>
                </Box>
                <Box sx={{ width: "200px" }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Upload date to"
                      value={uploadDateTo}
                      onChange={onUploadDateToChange}
                      slotProps={{
                        textField: { size: "small" },
                        field: { clearable: true, onClear: () => onUploadDateToChange(null) }
                      }}
                      minDate={uploadDateFrom ? uploadDateFrom : undefined}
                      maxDate={dayjs()}
                    />
                  </LocalizationProvider>
                </Box>
                {requesterList && (
                  <Box sx={{ width: "200px" }}>
                    <CustomAutocomplete
                      value={requesterFilter}
                      options={requesterList}
                      getOptionLabel={(option) => (option ? getRequesterName(requesterList, option.userId) : "")}
                      onChange={(e, val) => onRequesterFilterChange(val as RequesterRecord | undefined)}
                      label={"Requester"}
                      isOptionEqualToValue={(o, v) => o?.userId === v?.userId}
                      size="small"
                    />
                  </Box>
                )}
                <ViewButton onClick={onClearFilters}>
                  <FilterListRoundedIcon />
                </ViewButton>
              </>
            )}
          </Stack>
        </Stack>
        {!isMobile && viewToggle}
      </Stack>

      {/* Mobile Filter Modal */}
      <MyFilesFilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        statusFilter={statusFilter}
        onStatusFilterChange={onStatusFilterChange}
        statusOptions={statusOptions}
        uploadDateFrom={uploadDateFrom}
        onUploadDateFromChange={onUploadDateFromChange}
        uploadDateTo={uploadDateTo}
        onUploadDateToChange={onUploadDateToChange}
        requesterList={requesterList}
        requesterFilter={requesterFilter}
        onRequesterFilterChange={onRequesterFilterChange}
        onClearFilters={onClearFilters}
      />
    </Box>
  )
}

export default MyFilesFilter
